import type {z} from 'zod'
import {clientEnv as rawClientEnv} from './client-env'
import {clientSchema} from './schema.mjs'

type ClientEnv = z.infer<typeof clientSchema>

const isTestEnvironment = process.env.NODE_ENV === 'test'

const parsedClientEnv = clientSchema.safeParse(rawClientEnv)

/**
 * Format validation errors from Zod
 */
export const formatErrors = (
	errors: Record<string, unknown>
): (string | null)[] =>
	Object.entries(errors)
		.map(([name, value]) => {
			if (value && typeof value === 'object' && '_errors' in value) {
				const errorValue = value as {_errors: string[]}
				return `${name}: ${errorValue._errors.join(', ')}\n`
			}

			return null
		})
		.filter(Boolean)

if (!parsedClientEnv.success && !isTestEnvironment) {
	const message = `❌ Invalid environment variables:\n ${JSON.stringify(
		formatErrors(parsedClientEnv.error.format() as Record<string, unknown>)
	)}`

	// eslint-disable-next-line no-console -- We absolutely want to console log here so we can fix the issue
	console.error(message)
	throw new Error(message)
}

// Default test values for when env vars are not available during testing
const testDefaults: ClientEnv = {
	NEXT_PUBLIC_KONTENT_PROJECT_ID: 'test-project-id',
	NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING: 'test-connection-string',
	NEXT_PUBLIC_TOWER: 'pet',
	NEXT_PUBLIC_SEARCH_API_KEY: 'test-search-key',
	NEXT_PUBLIC_MAP_API_KEY: 'test-map-key',
	NEXT_PUBLIC_PARASITE_MAP_STYLE_ID: 'test-style-id',
	NEXT_PUBLIC_FAQ_COGNITIVE_SEARCH_API_KEY: 'test-faq-key',
	NEXT_PUBLIC_APP_URL: 'http://localhost:3000',
	NEXT_PUBLIC_FEATURE_FLAG_COUNTRY_SELECTOR: 'false',
	NEXT_PUBLIC_CWV_APP_INSIGHTS_SAMPLE_RATE: undefined,
	NEXT_PUBLIC_CWV_GTM_SAMPLE_RATE: undefined,
	NEXT_PUBLIC_CWV_DEBUG_LOGGING: false,
	NEXT_PUBLIC_NODE_ENV: 'test',
}

const getEnvData = (): ClientEnv => {
	if (parsedClientEnv.success) {
		return parsedClientEnv.data
	}
	if (isTestEnvironment) {
		return {...testDefaults, ...(rawClientEnv as Partial<ClientEnv>)}
	}
	// This should never be reached due to the throw above, but TypeScript needs it
	return testDefaults
}

const envData = getEnvData()

for (const key of Object.keys(envData)) {
	if (!key.startsWith('NEXT_PUBLIC_')) {
		// eslint-disable-next-line no-console -- We absolutely want to console log here so we can fix the issue
		console.warn('❌ Invalid public environment variable name:', key)

		throw new Error('Invalid public environment variable name')
	}
}

export const env: ClientEnv = envData
